/**
 * Date/time helpers for the calendar/scheduling layer.
 *
 * Storage rule (Phase 5): all `timestamptz` columns store **UTC** ISO strings.
 * Inputs (`<input type="datetime-local">`) work in the user's local TZ; we
 * always convert through `new Date(localInput)` and `.toISOString()` so the DB
 * persists an unambiguous UTC instant.
 *
 * Recurring rule: occurrences preserve the **local wall-clock** hour/minute of
 * the original event so DST transitions don't shift the displayed time.
 */

/** Convert an `<input type="datetime-local">` value (e.g. `2025-04-25T09:00`)
 *  to a UTC ISO string suitable for `timestamptz` storage. */
export function localInputToUtcIso(localInput: string): string {
  if (!localInput) return "";
  // `new Date("YYYY-MM-DDTHH:mm")` is interpreted in the runtime's local TZ.
  return new Date(localInput).toISOString();
}

/** Inverse: convert a stored UTC ISO string to a `datetime-local` input value
 *  (no seconds, no timezone), in the user's local TZ. */
export function utcIsoToLocalInput(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

export interface RecurringInput {
  /** First occurrence start, as `<input type="datetime-local">` value. */
  startLocalInput: string;
  endLocalInput: string;
  /** `<input type="date">` value for the last day to consider, inclusive. */
  untilLocalDate: string;
  mode: "daily" | "custom";
  /** For `custom`: weekdays (0=Sun) to INCLUDE. */
  includeDays?: number[];
  /** For `daily`: weekdays (0=Sun) to EXCLUDE. */
  excludeDays?: number[];
}

export interface RecurringOccurrence {
  startUtc: string;
  endUtc: string;
}

/**
 * Build occurrences between the start date and `untilLocalDate` inclusive.
 * Each occurrence preserves the wall-clock hour/minute of the original event,
 * so 9:00 AM stays 9:00 AM across DST changes.
 */
export function buildRecurringOccurrences(
  input: RecurringInput,
): RecurringOccurrence[] {
  const {
    startLocalInput,
    endLocalInput,
    untilLocalDate,
    mode,
    includeDays = [],
    excludeDays = [],
  } = input;

  if (!startLocalInput || !endLocalInput || !untilLocalDate) return [];

  const start = new Date(startLocalInput);
  const end = new Date(endLocalInput);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const startHours = start.getHours();
  const startMinutes = start.getMinutes();
  const endHours = end.getHours();
  const endMinutes = end.getMinutes();

  // End of day in local TZ for `untilLocalDate`.
  const untilDate = new Date(`${untilLocalDate}T23:59:59`);
  if (Number.isNaN(untilDate.getTime()) || untilDate < start) return [];

  const occurrences: RecurringOccurrence[] = [];

  // Iterate by calendar day in local TZ.
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endCursor = new Date(
    untilDate.getFullYear(),
    untilDate.getMonth(),
    untilDate.getDate(),
  );

  while (cursor <= endCursor) {
    const dow = cursor.getDay();
    let include = false;
    if (mode === "daily") {
      include = !excludeDays.includes(dow);
    } else {
      include = includeDays.includes(dow);
    }

    if (include) {
      const startLocal = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        cursor.getDate(),
        startHours,
        startMinutes,
        0,
        0,
      );
      const endLocal = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        cursor.getDate(),
        endHours,
        endMinutes,
        0,
        0,
      );
      // If end < start (shouldn't happen — UI validates) skip.
      if (endLocal > startLocal) {
        occurrences.push({
          startUtc: startLocal.toISOString(),
          endUtc: endLocal.toISOString(),
        });
      }
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return occurrences;
}
